import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, CheckCircle, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const Refunds = ({ user, onLogout }) => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchRefunds();
  }, []);

  const fetchRefunds = async () => {
    try {
      const response = await axios.get('/refunds');
      setRefunds(response.data);
    } catch (error) {
      toast.error('Error al cargar reembolsos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'PENDING': { label: 'Pendiente', icon: Clock, color: '#F57C00', bgColor: '#FFF3E0' },
      'APPROVED': { label: 'Aprobado', icon: CheckCircle, color: '#388E3C', bgColor: '#E8F5E9' },
      'REJECTED': { label: 'Rechazado', icon: XCircle, color: '#D32F2F', bgColor: '#FFEBEE' },
      'COMPLETED': { label: 'Completado', icon: CheckCircle, color: '#1976D2', bgColor: '#E3F2FD' }
    };
    return statusMap[status] || statusMap['PENDING'];
  };

  const filteredRefunds = refunds.filter(refund => {
    if (filter === 'all') return true;
    return refund.status === filter;
  });

  return (
    <Layout user={user} onLogout={onLogout} title="Reembolsos">
      <div className="space-y-6" data-testid="refunds-container">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#224049' }}>Reembolsos</h1>
            <p className="text-sm" style={{ color: '#666' }}>Gestiona los reembolsos automáticos por SLA</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-2">
              {['all', 'PENDING', 'APPROVED', 'COMPLETED', 'REJECTED'].map((status) => (
                <button
                  key={status}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                    filter === status ? 'shadow-md' : ''
                  }`}
                  style={{
                    backgroundColor: filter === status ? '#224049' : '#F0EBE3',
                    color: filter === status ? '#D4AD6A' : '#666'
                  }}
                  onClick={() => setFilter(status)}
                  data-testid={`filter-${status.toLowerCase()}`}
                >
                  {status === 'all' ? 'Todos' : getStatusInfo(status).label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-lg" style={{ color: '#224049' }}>Cargando...</div>
          </div>
        ) : filteredRefunds.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <DollarSign className="w-16 h-16 mx-auto mb-4" style={{ color: '#D9A27F' }} />
              <p className="text-lg" style={{ color: '#666' }}>No hay reembolsos</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredRefunds.map((refund) => {
              const statusInfo = getStatusInfo(refund.status);
              const StatusIcon = statusInfo.icon;

              return (
                <Card key={refund.id} className="card fade-in shadow-lg hover:shadow-xl" data-testid={`refund-card-${refund.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-full" style={{ backgroundColor: statusInfo.bgColor }}>
                          <StatusIcon className="w-6 h-6" style={{ color: statusInfo.color }} />
                        </div>
                        <div>
                          <CardTitle style={{ color: '#224049' }}>Reembolso {refund.id.substring(0, 8)}</CardTitle>
                          <p className="text-sm" style={{ color: '#666' }}>Pedido: {refund.order_id.substring(0, 8)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: '#D9A27F' }}>${refund.amount.toFixed(2)}</p>
                        <span
                          className="inline-block px-3 py-1 rounded-full text-xs font-semibold mt-2"
                          style={{ backgroundColor: statusInfo.bgColor, color: statusInfo.color }}
                        >
                          {statusInfo.label}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs font-medium" style={{ color: '#666' }}>Razón</p>
                        <p className="text-sm" style={{ color: '#224049' }}>{refund.reason}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#666' }}>Creado</p>
                          <p className="text-sm" style={{ color: '#224049' }}>
                            {new Date(refund.created_at).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                        {refund.processed_at && (
                          <div>
                            <p className="text-xs font-medium" style={{ color: '#666' }}>Procesado</p>
                            <p className="text-sm" style={{ color: '#224049' }}>
                              {new Date(refund.processed_at).toLocaleDateString('es-MX')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Refunds;